import React from 'react';
import { useNodes, useEdges } from '@xyflow/react';
import { Tag, Dropdown, Menu } from 'antd';
import { ClockCircleOutlined, GlobalOutlined, UserOutlined, ShoppingOutlined } from '@ant-design/icons';

const NodeInputHandler = ({ nodeId, onInsert, customInputs }) => {
  const nodes = useNodes();
  const edges = useEdges();

  const getPreviousNodes = () => {
    const incomingEdges = edges.filter(edge => edge.target === nodeId);
    return incomingEdges.map(edge => nodes.find(node => node.id === edge.source)).filter(Boolean);
  };

  const previousNodes = getPreviousNodes();
  const hasInputs = previousNodes.length > 0 || (customInputs && customInputs.length > 0);
  const woocommerceActive = window.wpAiWorkflowsSettings?.woocommerce_active === true;

  // Helper function to safely check nested properties
  const safeGet = (obj, path, defaultValue = undefined) => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : defaultValue;
    }, obj);
  };

  const renderMenuItem = (node) => {
    // Add safety checks for node structure
    if (!node || !node.data) {
      return null;
    }

    const nodeName = node.data?.nodeName || `${node.type} ${node.id}`;
    
    if (node.type === 'trigger') {
      switch (node.data?.triggerType) {
        case 'webhook':
          return renderWebhookMenu(node, nodeName);
        case 'gravityForms':
        case 'wpForms':
        case 'contactForm7':
        case 'ninjaForms':
          return renderGravityFormsMenu(node, nodeName);
        case 'wpCore':
          return renderWpCoreMenu(node, nodeName);
        case 'rss':
          return renderRSSMenu(node, nodeName);
        default:
          return renderDefaultMenuItem(node, nodeName);
      }
    } else if (node.type === 'firecrawl') {
      return renderFirecrawlMenu(node, nodeName);
    } else if (node.type === 'chat') {
      return renderChatMenu(node, nodeName);
    } else if (node.type === 'extractInformation') {
      return renderExtractInformationMenu(node, nodeName);
    } else if (node.type === 'research') {
      return (
        <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
          <Menu.Item key={`${node.id}-content`} onClick={() => onInsert(`[[content] from ${node.id}]`)}>
            Research Results
          </Menu.Item>
          <Menu.Item key={`${node.id}-citations`} onClick={() => onInsert(`[[citations] from ${node.id}]`)}>
            Source Citations
          </Menu.Item>
        </Menu.SubMenu>
      );
    } else if (node.type === 'aiModel' && 
               safeGet(node, 'data.openaiTools') && 
               (safeGet(node, 'data.openaiTools.webSearch.enabled', false) || 
                safeGet(node, 'data.openaiTools.fileSearch.enabled', false))) {
      return (
        <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
            <Menu.Item key={`${node.id}-content`} onClick={() => onInsert(`[[content] from ${node.id}]`)}>
                Generated Content
            </Menu.Item>
            <Menu.Item key={`${node.id}-citations`} onClick={() => onInsert(`[[citations] from ${node.id}]`)}>
                Citations
            </Menu.Item>
            <Menu.Item key={`${node.id}-search_results`} onClick={() => onInsert(`[[search_results] from ${node.id}]`)}>
                Search Results
            </Menu.Item>
        </Menu.SubMenu>
      );
    } else if (node.type === 'output' && node.data?.outputType === 'googleDrive') {
      return (
        <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
          <Menu.Item 
            key={`${node.id}-file-link`} 
            onClick={() => onInsert(`[[file_link] from ${node.id}]`)}
          >
            File Link
          </Menu.Item>
          <Menu.Item 
            key={`${node.id}-file-name`} 
            onClick={() => onInsert(`[[file_name] from ${node.id}]`)}
          >
            File Name
          </Menu.Item>
          <Menu.Item 
            key={`${node.id}-file-id`} 
            onClick={() => onInsert(`[[file_id] from ${node.id}]`)}
          >
            File ID
          </Menu.Item>
        </Menu.SubMenu>
      );
    } else {
      return renderDefaultMenuItem(node, nodeName);
    }
  };

  const renderWebhookMenu = (node, nodeName) => (
    <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
      {node.data?.webhookKeys && Array.isArray(node.data.webhookKeys) && node.data.webhookKeys.length > 0 ? (
        node.data.webhookKeys.map(key => (
          <Menu.Item key={`${node.id}-${key.key}`} onClick={() => onInsert(`[[${key.key}] from ${node.id}]`)}>
            {key.key}
          </Menu.Item>
        ))
      ) : (
        <Menu.Item key={`${node.id}-default`} onClick={() => onInsert(`[Input from ${node.id}]`)}>
          Webhook Data
        </Menu.Item>
      )}
    </Menu.SubMenu>
  );

  const renderGravityFormsMenu = (node, nodeName) => (
    <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
      {Array.isArray(node.data?.selectedFields) && node.data.selectedFields.length > 0 ? (
        node.data.selectedFields.map(field => (
          <Menu.Item key={`${node.id}-${field.id}`} onClick={() => onInsert(`[[${field.label}] from ${node.id}]`)}>
            {field.label}
          </Menu.Item>
        ))
      ) : (
        <Menu.Item key={`${node.id}-default`} onClick={() => onInsert(`[Input from ${node.id}]`)}>
          Form Data
        </Menu.Item>
      )}
    </Menu.SubMenu>
  );

  const renderWpCoreMenu = (node, nodeName) => {
    const wpCoreFields = getWpCoreFields(node.data?.selectedWpCoreTrigger);
    return (
      <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
        {wpCoreFields.length > 0 ? (
          wpCoreFields.map(field => (
            <Menu.Item key={`${node.id}-${field}`} onClick={() => onInsert(`[[${field}] from ${node.id}]`)}>
              {field}
            </Menu.Item>
          ))
        ) : (
          <Menu.Item key={`${node.id}-default`} onClick={() => onInsert(`[Input from ${node.id}]`)}>
            WP Core Data
          </Menu.Item>
        )}
      </Menu.SubMenu>
    );
  };

  const renderRSSMenu = (node, nodeName) => (
    <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
      <Menu.SubMenu key={`${node.id}-latest`} title="Latest Item">
        <Menu.Item key={`${node.id}-latest-title`} onClick={() => onInsert(`[[latest.title] from ${node.id}]`)}>
          Title
        </Menu.Item>
        <Menu.Item key={`${node.id}-latest-link`} onClick={() => onInsert(`[[latest.link] from ${node.id}]`)}>
          Link
        </Menu.Item>
        <Menu.Item key={`${node.id}-latest-description`} onClick={() => onInsert(`[[latest.description] from ${node.id}]`)}>
          Description
        </Menu.Item>
        <Menu.Item key={`${node.id}-latest-media`} onClick={() => onInsert(`[[latest.media_url] from ${node.id}]`)}>
          Media URL
        </Menu.Item>
        <Menu.Item key={`${node.id}-latest-pubDate`} onClick={() => onInsert(`[[latest.pubDate] from ${node.id}]`)}>
          Publication Date
        </Menu.Item>
      </Menu.SubMenu>
      <Menu.SubMenu key={`${node.id}-feed`} title="Feed Info">
        <Menu.Item key={`${node.id}-feed-title`} onClick={() => onInsert(`[[feed_title] from ${node.id}]`)}>
          Feed Title
        </Menu.Item>
        <Menu.Item key={`${node.id}-feed-description`} onClick={() => onInsert(`[[feed_description] from ${node.id}]`)}>
          Feed Description
        </Menu.Item>
        <Menu.Item key={`${node.id}-feed-link`} onClick={() => onInsert(`[[feed_link] from ${node.id}]`)}>
          Feed Link
        </Menu.Item>
      </Menu.SubMenu>
      <Menu.SubMenu key={`${node.id}-items`} title="All Items">
        <Menu.Item key={`${node.id}-items-all`} onClick={() => onInsert(`[[items] from ${node.id}]`)}>
          All Items
        </Menu.Item>
      </Menu.SubMenu>
    </Menu.SubMenu>
  );

  const renderExtractInformationMenu = (node, nodeName) => (
    <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
      {Array.isArray(node.data?.extractionFields) && node.data.extractionFields.length > 0 ? (
        node.data.extractionFields.map(field => (
          <Menu.Item 
            key={`${node.id}-${field.name}`} 
            onClick={() => onInsert(`[[${field.name}] from ${node.id}]`)}
          >
            {field.name} {field.isList ? '(List)' : ''}
          </Menu.Item>
        ))
      ) : (
        <Menu.Item key={`${node.id}-default`} onClick={() => onInsert(`[Input from ${node.id}]`)}>
          Extracted Data
        </Menu.Item>
      )}
    </Menu.SubMenu>
  );

  const renderFirecrawlMenu = (node, nodeName) => (
    <Menu.SubMenu key={node.id} title={`Input from ${nodeName}`}>
      <Menu.Item key={`${node.id}-content`} onClick={() => onInsert(`[Input from ${node.id}]`)}>
        Full Content
      </Menu.Item>
      {node.data?.format === 'extract' && Array.isArray(node.data?.extractFields) && (
        <Menu.SubMenu key={`${node.id}-extract`} title="Extracted Data">
          {node.data.extractFields.map(field => (
            <Menu.Item 
              key={`${node.id}-${field.name}`} 
              onClick={() => onInsert(`[[extract.website.${field.name}] from ${node.id}]`)}
            >
              {field.name} ({field.type})
            </Menu.Item>
          ))}
        </Menu.SubMenu>
      )}
    </Menu.SubMenu>
  );

  const renderDefaultMenuItem = (node, nodeName) => (
    <Menu.Item key={node.id} onClick={() => onInsert(`[Input from ${node.id}]`)}>
      Input from {nodeName}
    </Menu.Item>
  );

  const getWpCoreFields = (triggerType) => {
    switch (triggerType) {
      case 'publish_post':
        return ['post_id', 'post_type', 'post_title', 'post_content', 'post_excerpt', 'post_author', 'post_date', 'post_status', 'post_categories', 'post_tags', 'post_url'];
      case 'user_register':
        return ['user_id', 'user_login', 'user_email', 'user_registered', 'display_name', 'first_name', 'last_name', 'user_role'];
      case 'wp_insert_comment':
        return ['comment_id', 'comment_post_id', 'comment_author', 'comment_author_email', 'comment_author_url', 'comment_content', 'comment_type', 'comment_parent', 'user_id', 'comment_date', 'comment_approved', 'post_type'];
      case 'wp_login':
        return ['user_id', 'user_login', 'user_email', 'display_name', 'user_role'];
      case 'transition_post_status':
        return ['post_id', 'post_type', 'post_title', 'post_content', 'post_excerpt', 'post_categories', 'post_tags', 'post_author', 'old_status', 'new_status', 'post_date', 'post_modified', 'post_url'];
      default:
        return [];
    }
  };

  const renderChatMenu = (node, nodeName) => {
    // Safety checks for chat menu
    if (!Array.isArray(node.data?.actions)) {
      return renderDefaultMenuItem(node, nodeName);
    }

    // Find which handle is connected to this target node
    const connectionHandle = edges.find(edge => 
      edge.source === node.id && 
      edge.target === nodeId
    )?.sourceHandle;
  
    if (!connectionHandle) {
      return renderDefaultMenuItem(node, nodeName);
    }
  
    // Find the specific action
    const action = node.data.actions.find(action => action.id === connectionHandle);
    if (!action || !Array.isArray(action.fields)) {
      return renderDefaultMenuItem(node, nodeName);
    }
  
    // Create menu for just this action
    return (
      <Menu.SubMenu key={node.id} title={`${action.name} Data from ${nodeName}`}>
        {action.fields.map(field => (
          <Menu.Item
            key={`${node.id}-${action.id}-${field.name}`}
            onClick={() => onInsert(`[[${action.id}.${field.name}] from ${node.id}]`)}
          >
            {field.name}
          </Menu.Item>
        ))}
        <Menu.Item
          key={`${node.id}-${action.id}-all`}
          onClick={() => onInsert(`[[${action.id}] from ${node.id}]`)}
        >
          All Fields
        </Menu.Item>
      </Menu.SubMenu>
    );
  };

  const menu = (
    <Menu>
      {/* First render custom inputs if provided */}
      {Array.isArray(customInputs) && customInputs.map(input => (
        <Menu.Item 
          key={`custom-${input.key}`} 
          onClick={() => onInsert(input.value)}
        >
          {input.label}
        </Menu.Item>
      ))}
      {/* Then render the regular node inputs */}
      {previousNodes.map(node => {
        try {
          return renderMenuItem(node);
        } catch (error) {
          console.error('Error rendering menu item for node:', node.id, error);
          return renderDefaultMenuItem(node, `${node.type} ${node.id}`);
        }
      })}
    </Menu>
  );

  const dynamicVariablesMenu = (
    <Menu>
      <Menu.SubMenu key="datetime" icon={<ClockCircleOutlined />} title="Date & Time">
        <Menu.Item key="current_date" onClick={() => onInsert("{{current_date}}")}>
          Current Date (YYYY-MM-DD)
        </Menu.Item>
        <Menu.Item key="current_time" onClick={() => onInsert("{{current_time}}")}>
          Current Time (HH:mm:ss)
        </Menu.Item>
        <Menu.Item key="current_datetime" onClick={() => onInsert("{{current_datetime}}")}>
          Current Date & Time
        </Menu.Item>
        <Menu.Item key="current_timestamp" onClick={() => onInsert("{{current_timestamp}}")}>
          Current Timestamp
        </Menu.Item>
        <Menu.Item key="yesterday" onClick={() => onInsert("{{yesterday}}")}>
          Yesterday's Date
        </Menu.Item>
        <Menu.Item key="tomorrow" onClick={() => onInsert("{{tomorrow}}")}>
          Tomorrow's Date
        </Menu.Item>
        <Menu.Item key="current_month" onClick={() => onInsert("{{current_month}}")}>
          Current Month Name
        </Menu.Item>
        <Menu.Item key="current_year" onClick={() => onInsert("{{current_year}}")}>
          Current Year
        </Menu.Item>
        <Menu.Item key="current_day" onClick={() => onInsert("{{current_day}}")}>
          Current Day Name
        </Menu.Item>
      </Menu.SubMenu>

      <Menu.SubMenu key="wordpress" icon={<GlobalOutlined />} title="WordPress">
        <Menu.Item key="site_name" onClick={() => onInsert("{{site_name}}")}>
          Site Name
        </Menu.Item>
        <Menu.Item key="site_url" onClick={() => onInsert("{{site_url}}")}>
          Site URL
        </Menu.Item>
        <Menu.Item key="admin_email" onClick={() => onInsert("{{admin_email}}")}>
          Admin Email
        </Menu.Item>

        <Menu.Divider />
        <Menu.ItemGroup title="Current Post/Page">
          <Menu.Item key="post_id" onClick={() => onInsert("{{post_id}}")}>
            Post/Page ID
          </Menu.Item>
          <Menu.Item key="post_title" onClick={() => onInsert("{{post_title}}")}>
            Post/Page Title
          </Menu.Item>
          <Menu.Item key="post_excerpt" onClick={() => onInsert("{{post_excerpt}}")}>
            Post Excerpt
          </Menu.Item>
          <Menu.Item key="post_content" onClick={() => onInsert("{{post_content}}")}>
            Post/Page Content
          </Menu.Item>
          <Menu.Item key="post_author" onClick={() => onInsert("{{post_author}}")}>
            Post Author
          </Menu.Item>
          <Menu.Item key="post_date" onClick={() => onInsert("{{post_date}}")}>
            Publication Date
          </Menu.Item>
          <Menu.Item key="post_modified_date" onClick={() => onInsert("{{post_modified_date}}")}>
            Last Modified Date
          </Menu.Item>
          <Menu.Item key="post_type" onClick={() => onInsert("{{post_type}}")}>
            Post Type
          </Menu.Item>
          <Menu.Item key="post_status" onClick={() => onInsert("{{post_status}}")}>
            Post Status
          </Menu.Item>
          <Menu.Item key="post_url" onClick={() => onInsert("{{post_url}}")}>
            Post URL
          </Menu.Item>
          <Menu.Item key="post_categories" onClick={() => onInsert("{{post_categories}}")}>
            Categories
          </Menu.Item>
          <Menu.Item key="post_tags" onClick={() => onInsert("{{post_tags}}")}>
            Tags
          </Menu.Item>
        </Menu.ItemGroup>
      </Menu.SubMenu>

      <Menu.SubMenu key="user" icon={<UserOutlined />} title="Current User">
        <Menu.Item key="current_user" onClick={() => onInsert("{{current_user}}")}>
          Username
        </Menu.Item>
        <Menu.Item key="current_user_email" onClick={() => onInsert("{{current_user_email}}")}>
          User Email
        </Menu.Item>
        <Menu.Item key="current_user_id" onClick={() => onInsert("{{current_user_id}}")}>
          User ID
        </Menu.Item>
      </Menu.SubMenu>

      <Menu.SubMenu key="woocommerce" icon={<ShoppingOutlined />} title="WooCommerce">
        <Menu.ItemGroup title="Current Product">
          <Menu.Item key="product_id" onClick={() => onInsert("{{product_id}}")}>
            Product ID
          </Menu.Item>
          <Menu.Item key="product_name" onClick={() => onInsert("{{product_name}}")}>
            Product Name
          </Menu.Item>
          <Menu.Item key="product_price" onClick={() => onInsert("{{product_price}}")}>
            Current Price
          </Menu.Item>
          <Menu.Item key="product_regular_price" onClick={() => onInsert("{{product_regular_price}}")}>
            Regular Price
          </Menu.Item>
          <Menu.Item key="product_sale_price" onClick={() => onInsert("{{product_sale_price}}")}>
            Sale Price
          </Menu.Item>
          <Menu.Item key="product_sku" onClick={() => onInsert("{{product_sku}}")}>
            SKU
          </Menu.Item>
          <Menu.Item key="product_stock" onClick={() => onInsert("{{product_stock}}")}>
            Stock Quantity
          </Menu.Item>
          <Menu.Item key="product_stock_status" onClick={() => onInsert("{{product_stock_status}}")}>
            Stock Status
          </Menu.Item>
          <Menu.Item key="product_category" onClick={() => onInsert("{{product_category}}")}>
            Product Categories
          </Menu.Item>
          <Menu.Item key="product_type" onClick={() => onInsert("{{product_type}}")}>
            Product Type
          </Menu.Item>
          <Menu.Item key="product_url" onClick={() => onInsert("{{product_url}}")}>
            Product URL
          </Menu.Item>
          <Menu.Item key="product_rating" onClick={() => onInsert("{{product_rating}}")}>
            Average Rating
          </Menu.Item>
          <Menu.Item key="product_review_count" onClick={() => onInsert("{{product_review_count}}")}>
            Review Count
          </Menu.Item>
        </Menu.ItemGroup>

        <Menu.ItemGroup title="Cart">
          <Menu.Item key="cart_total" onClick={() => onInsert("{{cart_total}}")}>
            Cart Total
          </Menu.Item>
          <Menu.Item key="cart_subtotal" onClick={() => onInsert("{{cart_subtotal}}")}>
            Cart Subtotal
          </Menu.Item>
          <Menu.Item key="cart_item_count" onClick={() => onInsert("{{cart_item_count}}")}>
            Items in Cart
          </Menu.Item>
          <Menu.Item key="cart_url" onClick={() => onInsert("{{cart_url}}")}>
            Cart URL
          </Menu.Item>
          <Menu.Item key="checkout_url" onClick={() => onInsert("{{checkout_url}}")}>
            Checkout URL
          </Menu.Item>
        </Menu.ItemGroup>
      </Menu.SubMenu>

      <Menu.SubMenu key="random" title="Random Generators">
        <Menu.Item key="random_number" onClick={() => onInsert("{{random_number}}")}>
          Random Number (0-100)
        </Menu.Item>
        <Menu.Item key="random_number_1000" onClick={() => onInsert("{{random_number_1000}}")}>
          Random Number (0-1000)
        </Menu.Item>
        <Menu.Item key="random_string" onClick={() => onInsert("{{random_string}}")}>
          Random String (8 chars)
        </Menu.Item>
        <Menu.Item key="unique_id" onClick={() => onInsert("{{unique_id}}")}>
          Unique ID
        </Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  return (
    <div style={{ display: 'flex', gap: '8px', marginTop: 8 }}>
      {/* Existing Input Handler */}
      {hasInputs ? (
        <Dropdown overlay={menu} trigger={['click']}>
          <Tag color="success" style={{ cursor: 'pointer' }}>
            {(previousNodes.length + (customInputs?.length || 0))} {' '}
            {(previousNodes.length + (customInputs?.length || 0)) === 1 ? 'Input' : 'Inputs'} Available
          </Tag>
        </Dropdown>
      ) : (
        <Tag color="error">
          No Inputs
        </Tag>
      )}
      
      {/* New Dynamic Variables Button */}
      <Dropdown overlay={dynamicVariablesMenu} trigger={['click']}>
        <Tag color="orange" style={{ cursor: 'pointer' }}>
          <ClockCircleOutlined /> Dynamic Variables
        </Tag>
      </Dropdown>
    </div>
  );
};

export default NodeInputHandler;